import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider
} from '@mui/material'
import { questionsApi, assessmentsApi } from '../../api/assessments'
import { FormSection } from '../../components/assessments/FormSection'
import { OptionInput } from '../../components/assessments/OptionInput'
import { SortableQuestionsList } from '../../components/assessments/SortableQuestionsList'
import type { Question, QuestionType } from '../../types/assessments'
import { STATIC_LIKERT_OPTIONS } from '../../types/assessments'

export const QuestionBuilderPage: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])

  const [formData, setFormData] = useState<Omit<Question, 'id' | 'uuid'>>({
    assessmentId: assessmentId || '',
    order: 1,
    question: '',
    type: 'MCQ',
    options: STATIC_LIKERT_OPTIONS,
    status: 'ACTIVE'
  })

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!assessmentId) return

      try {
        setLoading(true)
        const fetchedQuestions = await questionsApi.getByAssessmentId(assessmentId)
        const sortedQuestions = fetchedQuestions.sort((a, b) => a.order - b.order)
        setQuestions(sortedQuestions as Question[])
        setFormData((prev) => ({
          ...prev,
          order: sortedQuestions.length + 1
        }))
      } catch (err: any) {
        setError(err.message || 'Failed to load questions')
      } finally {
        setLoading(false)
      }
    }

    fetchQuestions()
  }, [assessmentId])

  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!assessmentId) return

    try {
      setSaving(true)
      setError(null)

      const newQuestion = await questionsApi.create(assessmentId, {
        ...formData,
        order: questions.length + 1
      })

      setQuestions([...questions, newQuestion as Question])
      setFormData({
        assessmentId,
        order: questions.length + 2,
        question: '',
        type: 'MCQ',
        options: STATIC_LIKERT_OPTIONS,
        status: 'ACTIVE'
      })
    } catch (err: any) {
      setError(err.message || 'Failed to add question')
    } finally {
      setSaving(false)
    }
  }

  const handleEditQuestion = (question: Question) => {
    setFormData({
      assessmentId: question.assessmentId,
      order: question.order,
      question: question.question,
      type: question.type,
      options: STATIC_LIKERT_OPTIONS,
      status: question.status
    })
  }

  const handleDeleteQuestion = async (questionId: number | string) => {
    if (!assessmentId) return

    try {
      await questionsApi.delete(assessmentId, questionId as number)
      setQuestions(questions.filter((q) => (q.id || q.uuid) !== questionId))
    } catch (err: any) {
      setError(err.message || 'Failed to delete question')
    }
  }

  const handleReorder = async (reorderedQuestions: Question[]) => {
    if (!assessmentId) return

    try {
      setQuestions(reorderedQuestions)

      // Call API to update order
      const orders = reorderedQuestions.map((q, index) => ({
        questionId: q.id || 0,
        order: index + 1
      }))

      await questionsApi.reorder(assessmentId, { orders })
    } catch (err: any) {
      setError(err.message || 'Failed to reorder questions')
      // Revert on error
      const fetchQuestions = async () => {
        const fetchedQuestions = await questionsApi.getByAssessmentId(assessmentId)
        const sortedQuestions = fetchedQuestions.sort((a, b) => a.order - b.order)
        setQuestions(sortedQuestions as Question[])
      }
      fetchQuestions()
    }
  }

  if (loading) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant='h4' sx={{ fontWeight: 600 }}>
          Question Builder
        </Typography>
        <Button variant='outlined' onClick={() => navigate(`/assessments/preview/${assessmentId}`)}>
          Preview Assessment
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 4 }}>
        {/* Question Form */}
        <Box>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant='h6' sx={{ mb: 3, fontWeight: 600 }}>
              Add/Edit Question
            </Typography>

            <form onSubmit={handleAddQuestion}>
              <TextField
                fullWidth
                label='Question'
                multiline
                rows={3}
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                select
                label='Type'
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as QuestionType })}
                sx={{ mb: 2 }}
              >
                <MenuItem value='MCQ'>MCQ</MenuItem>
                <MenuItem value='SINGLE'>Single Choice</MenuItem>
                <MenuItem value='MULTIPLE'>Multiple Choice</MenuItem>
                <MenuItem value='DESCRIPTIVE'>Descriptive</MenuItem>
              </TextField>

              <Box sx={{ mb: 2 }}>
                <Typography variant='body2' sx={{ mb: 1, fontWeight: 500 }}>
                  Options
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {STATIC_LIKERT_OPTIONS.map((option, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <Typography
                        variant='h6'
                        sx={{
                          fontWeight: 700,
                          color: '#495057',
                          minWidth: '30px'
                        }}
                      >
                        {index + 1}
                      </Typography>
                      <Typography sx={{ color: '#495057', flex: 1 }}>
                        {option.split(' - ')[1]}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  These options are standard for all assessment questions and cannot be modified.
                </Typography>
              </Box>

              <Button type='submit' variant='contained' fullWidth disabled={saving} sx={{ mt: 2 }}>
                {saving ? <CircularProgress size={24} /> : 'Add Question'}
              </Button>
            </form>
          </Paper>
        </Box>

        {/* Questions List */}
        <Box>
          <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
            Questions ({questions.length})
          </Typography>

          {questions.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color='text.secondary'>
                No questions added yet. Start by adding your first question.
              </Typography>
            </Paper>
          ) : (
            <SortableQuestionsList
              questions={questions}
              onReorder={handleReorder}
              onEdit={handleEditQuestion}
              onDelete={handleDeleteQuestion}
            />
          )}
        </Box>
      </Box>
    </Container>
  )
}
